import apiClient from './apiClient'
import logger from '@/utils/logger'
import type {
  Subscription,
  LineAmendment,
  LineFreeze,
  LineAmendmentRequest,
  LineFreezeRequest,
  CreateSubscriptionLineRequest
} from '@/types'

interface SubscriptionsResponse {
  data: Subscription[]
  message: string
  success: boolean
  /**
   * POST /subscriptions/refresh only: false means the backend saw the CRM
   * still processing a change and persisted nothing, so retry shortly.
   * Missing (older backend) counts as settled.
   */
  synced?: boolean
}

interface ApiSuccessResponse {
  data: unknown
  message: string
  success: boolean
}

class SubscriptionService {
  /**
   * Get user's subscriptions
   */
  async getSubscriptions(): Promise<Subscription[]> {
    const response = await apiClient.get('/api/auth/subscriptions')
    return (response as unknown as SubscriptionsResponse).data || []
  }

  /**
   * Refresh subscriptions from external service.
   *
   * skipMutationLock: this is a background reconciliation, not a user save.
   * The mutation that triggered it already showed the blocking overlay;
   * without the skip, every refresh (and every settle retry) re-raises the
   * global spinner, which reads as the app being stuck.
   */
  async refreshSubscriptions(): Promise<Subscription[]> {
    const response = await apiClient.post('/api/auth/subscriptions/refresh', {}, {
      skipMutationLock: true,
    })
    return (response as unknown as SubscriptionsResponse).data || []
  }

  /**
   * Refresh subscriptions, retrying while the backend reports the CRM is
   * still processing the customer's change (BUG-5, backend docs/bugs.md).
   * The backend persists only a settled CRM state and returns synced: false
   * when it is not settled yet; we retry on a delay up to a cap, and return
   * the latest data either way (the optimistic UI covers the gap).
   */
  async refreshSubscriptionsUntilSettled(maxAttempts = 5, delayMs = 5000): Promise<Subscription[]> {
    let data: Subscription[] = []

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      // skipMutationLock: see refreshSubscriptions. Retries especially must
      // not flash the global spinner once per attempt.
      const response = (await apiClient.post(
        '/api/auth/subscriptions/refresh',
        {},
        { skipMutationLock: true }
      )) as unknown as SubscriptionsResponse

      data = response.data || []

      if (response.synced !== false) {
        return data
      }

      logger.debug('[SubscriptionService] CRM still processing, retrying refresh', {
        attempt,
        maxAttempts,
      })

      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, delayMs))
      }
    }

    return data
  }

  /**
   * Get pending amendments for a subscription
   */
  async getAmendments(subscriptionId: string): Promise<LineAmendment[]> {
    const response = await apiClient.get(`/api/auth/subscriptions/${subscriptionId}/amendments`)
    return (response as unknown as { data: LineAmendment[] }).data || []
  }

  /** Temporary product pauses (Line Freezes) on a subscription, DF-350. */
  async getFreezes(subscriptionId: string): Promise<LineFreeze[]> {
    const response = await apiClient.get(`/api/auth/subscriptions/${subscriptionId}/freezes`)
    return (response as unknown as { data: LineFreeze[] }).data || []
  }

  /** Cancel a temporary product pause, so its deliveries go ahead. DF-350. */
  async cancelFreeze(freezeId: string): Promise<void> {
    await apiClient.delete(`/api/auth/subscription-lines/freezes/${freezeId}`)
  }

  /**
   * Cancel (delete) an amendment in CRM
   */
  async cancelAmendment(amendmentGuid: string, options: { checksUnknownOutcome?: boolean } = {}): Promise<void> {
    await apiClient.delete(`/api/auth/subscription-lines/amendments/${amendmentGuid}`, options)
  }

  /**
   * Create a line amendment to change quantity or remove an item
   */
  /** Undo a scheduled removal, so the line carries on delivering. */
  async clearLineEndDate(lineId: string): Promise<void> {
    await apiClient.delete(`/api/auth/subscription-lines/${lineId}/end-date`)
  }

  async createLineAmendment(
    request: LineAmendmentRequest,
    options: { checksUnknownOutcome?: boolean } = {}
  ): Promise<ApiSuccessResponse> {
    logger.debug('[SubscriptionService] Creating line amendment', {
      subscription_line_id: request.subscription_line_id,
      quantity: request.quantity,
      effective_from: request.effective_from
    })

    try {
      const response = await apiClient.post('/api/auth/subscription-lines/amendments', request, options)
      
      logger.debug('[SubscriptionService] Line amendment created successfully', {
        subscription_line_id: request.subscription_line_id,
        response
      })
      
      return response as unknown as ApiSuccessResponse
    } catch (error) {
      logger.error('[SubscriptionService] Failed to create line amendment', {
        subscription_line_id: request.subscription_line_id,
        request,
        error
      })
      throw error
    }
  }

  /**
   * Temporarily pause a product for a date range (DF-350). One Line Freeze in
   * CRM, instead of ending the line and adding a resume line.
   */
  async freezeLine(request: LineFreezeRequest, options: { checksUnknownOutcome?: boolean } = {}): Promise<ApiSuccessResponse> {
    logger.debug('[SubscriptionService] Freezing line (temporary pause)', {
      subscription_line_id: request.subscription_line_id,
      effective_from: request.effective_from,
      effective_to: request.effective_to,
    })

    try {
      const response = await apiClient.post('/api/auth/subscription-lines/freeze', request, options)
      return response as unknown as ApiSuccessResponse
    } catch (error) {
      logger.error('[SubscriptionService] Failed to freeze line', { request, error })
      throw error
    }
  }

  /**
   * Create a new subscription line (add new item)
   */
  async createSubscriptionLine(
    request: CreateSubscriptionLineRequest,
    options: { checksUnknownOutcome?: boolean } = {}
  ): Promise<ApiSuccessResponse> {
    logger.debug('[SubscriptionService] Creating subscription line', {
      subscription_id: request.subscription_id,
      product_id: request.product_id,
      quantity: request.quantity,
      delivery_day: request.delivery_day,
      start_date: request.start_date
    })

    try {
      const response = await apiClient.post('/api/auth/subscription-lines', request, options)
      
      logger.debug('[SubscriptionService] Subscription line created successfully', {
        subscription_id: request.subscription_id,
        product_id: request.product_id,
        response
      })
      
      return response as unknown as ApiSuccessResponse
    } catch (error) {
      logger.error('[SubscriptionService] Failed to create subscription line', {
        request,
        error
      })
      throw error
    }
  }

  /** Save the client-facing delivery note back to CRM (tsg_deliverynotes). */
  async updateDeliveryNotes(subscriptionId: string, notes: string): Promise<void> {
    await apiClient.patch(`/api/auth/subscriptions/${subscriptionId}/delivery-notes`, {
      delivery_notes: notes,
    })
  }
}

export default new SubscriptionService()
